import { z } from 'zod';

// `BOUNCED`: the latest Belehrung was accepted and the provider then reported a bounce.
export const BROKERAGE_NOTICE_STATES = ['NOT_SENT', 'SENT', 'FAILED', 'BOUNCED'] as const;
export const BROKERAGE_NOTICE_BLOCK_REASONS = [
  'NOT_ACTIVE',
  'ALREADY_CONCLUDED',
  'NO_EMAIL',
  'NO_COMMISSION',
  'COMPANY_DATA_INCOMPLETE',
  'PRIVACY_URL_MISSING',
  // Checked last: no Belehrung goes out under a draft Muster.
  'MUSTER_NOT_SIGNED_OFF',
] as const;
export const BROKERAGE_RECEIPT_STATES = ['PENDING', 'SENT', 'FAILED'] as const;

export const BrokerageNoticeStateSchema = z.enum(BROKERAGE_NOTICE_STATES);
export const BrokerageNoticeBlockReasonSchema = z.enum(BROKERAGE_NOTICE_BLOCK_REASONS);

// Stored state only: no e-mail address, no IP, nothing of the Nachweis. It rides on every `Prospect` (list, detail,
// the MCP reads), so it is computed from one LEFT JOIN, never from a cross-module call.
export const BrokerageAgreementSummarySchema = z.object({
  notice: z.object({
    state: BrokerageNoticeStateSchema,
    sentAt: z.iso.datetime().nullable(),
    reminderSentAt: z.iso.datetime().nullable(),
  }),
  contract: z
    .object({
      concludedAt: z.iso.datetime(),
      earlyStartRequested: z.boolean(),
      withdrawalPeriodEndsOn: z.iso.date(),
      acceptanceReplyReceivedAt: z.iso.datetime().nullable(),
      contractNumber: z.string(),
    })
    .nullable(),
  withdrawal: z
    .object({
      receivedAt: z.iso.datetime(),
      reason: z.string().nullable(),
      receiptState: z.enum(BROKERAGE_RECEIPT_STATES),
    })
    .nullable(),
});

// The member's card: the summary plus what only the app shows.
export const BrokerageAgreementCardSchema = z.object({
  summary: BrokerageAgreementSummarySchema,
  noticeSentTo: z.string().nullable(),
  blockedBy: BrokerageNoticeBlockReasonSchema.nullable(),
});

export const BrokerageNoticeReadinessSchema = z.object({
  blockedBy: BrokerageNoticeBlockReasonSchema.nullable(),
});

export const GetBrokerageAgreementPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});

export const GetBrokerageAgreementResponseSchema = BrokerageAgreementCardSchema;

export const BrokerageNoticeReadinessPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

export const BrokerageNoticeReadinessQuerySchema = z.object({
  contactId: z.uuid(),
  propertyId: z.uuid(),
});

export const BrokerageNoticeReadinessResponseSchema = BrokerageNoticeReadinessSchema;

export const SendBrokerageNoticePathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const SendBrokerageNoticeBodySchema = z.object({}).strict();

export const MarkBrokerageAcceptanceReplyPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const MarkBrokerageAcceptanceReplyBodySchema = z.object({}).strict();

export type BrokerageReceiptState = (typeof BROKERAGE_RECEIPT_STATES)[number];
export type BrokerageNoticeState = z.infer<typeof BrokerageNoticeStateSchema>;
export type BrokerageNoticeBlockReason = z.infer<typeof BrokerageNoticeBlockReasonSchema>;
export type BrokerageAgreementSummary = z.infer<typeof BrokerageAgreementSummarySchema>;
export type BrokerageAgreementCard = z.infer<typeof BrokerageAgreementCardSchema>;
export type BrokerageNoticeReadiness = z.infer<typeof BrokerageNoticeReadinessSchema>;
export type GetBrokerageAgreementPathParams = z.infer<typeof GetBrokerageAgreementPathParamsSchema>;
export type BrokerageNoticeReadinessPathParams = z.infer<typeof BrokerageNoticeReadinessPathParamsSchema>;
export type BrokerageNoticeReadinessQuery = z.infer<typeof BrokerageNoticeReadinessQuerySchema>;
export type SendBrokerageNoticePathParams = z.infer<typeof SendBrokerageNoticePathParamsSchema>;
export type SendBrokerageNoticeBody = z.infer<typeof SendBrokerageNoticeBodySchema>;
export type MarkBrokerageAcceptanceReplyPathParams = z.infer<typeof MarkBrokerageAcceptanceReplyPathParamsSchema>;
export type MarkBrokerageAcceptanceReplyBody = z.infer<typeof MarkBrokerageAcceptanceReplyBodySchema>;
